/**
 * NOON ALL DAY — Beyond Europe market-interest form (CH-189, lives in #ship-to-dialog).
 *
 * Klaviyo client API only (public key + list ID from theme settings — no private credentials
 * in storefront code). Geolocation preselect uses Shopify's native
 * /browsing_context_suggestions.json — no external IP service, never auto-submits.
 * Country switching in the dialog is untouched (native localization form).
 *
 * FALLBACK: this file is purely additive. If Klaviyo is unreachable the submit shows cfg.error and
 * the visitor stays in the modal; country-switching (native localization form) is unaffected. The
 * whole section can be killed from the theme editor (nad_ship_to_interest_enabled) without a code
 * revert.
 */
(function () {
  if (window.__nadInterestInit) return; // guard against Theme Editor section reloads
  window.__nadInterestInit = true;

  // Klaviyo API revision. Revisions are stable ~1yr from release then fall-forward for a further
  // year. CH-189 bumped 2024-10-15 (retired) → 2026-01-15. Bump to the latest live revision from
  // Klaviyo's API changelog for maximum runway — it's this one constant.
  var REVISION = '2026-01-15';
  var detected = null;

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function readConfig() {
    var el = document.querySelector('[data-nad-interest-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function preselect(root, cfg) {
    if (!cfg.detect) return;
    fetch(cfg.rootUrl + 'browsing_context_suggestions.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var country = json && json.detected_values && json.detected_values.country;
        if (!country) return;
        detected = (country.handle || '').toUpperCase();
        var select = root.querySelector('[data-nad-interest-country]');
        var option = select && select.querySelector('option[value="' + detected + '"]');
        if (!option) return; // detected country is not a future market — leave everything as is
        select.value = detected;
        var copy = root.querySelector('[data-nad-interest-copy]');
        if (copy && copy.dataset.copyDetected) {
          copy.textContent = copy.dataset.copyDetected.replace('[country]', option.textContent.trim());
        }
        emit('nad:ship-to:interest-preselected', { country: detected });
      })
      .catch(function () {});
  }

  function subscribe(cfg, email, countryCode, countryName) {
    var properties = {
      market_interest_country: countryName,
      market_interest_country_code: countryCode,
      interest_source: 'Country selector',
      interest_date: new Date().toISOString(),
      storefront_language: cfg.language
    };
    if (detected) properties.detected_country = detected;

    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Country selector',
            profile: { data: { type: 'profile', attributes: { email: email, properties: properties } } }
          },
          relationships: { list: { data: { type: 'list', id: cfg.listId } } }
        }
      })
    }).then(function (response) {
      if (!response.ok) throw new Error('subscription ' + response.status);
      // Event is fire-and-forget: its failure must not fail the submission.
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(cfg.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: properties,
              metric: { data: { type: 'metric', attributes: { name: 'Submitted Market Interest' } } },
              // CH-191: carry the properties on the event's PROFILE too — Klaviyo writes profile
              // properties from events reliably (the /client/subscriptions endpoint does not), so the
              // selected country lands as a profile property (visible + segmentable), not just an event prop.
              profile: { data: { type: 'profile', attributes: { email: email, properties: properties } } }
            }
          }
        })
      }).catch(function () {});
    });
  }

  function init() {
    var root = document.querySelector('[data-nad-interest]');
    var cfg = readConfig();
    if (!root || !cfg || !cfg.companyId || !cfg.listId) return;

    preselect(root, cfg);

    if ('IntersectionObserver' in window) {
      var seen = false;
      new IntersectionObserver(function (entries, observer) {
        if (!seen && entries.some(function (e) { return e.isIntersecting; })) {
          seen = true;
          emit('nad:ship-to:interest-viewed');
          observer.disconnect();
        }
      }).observe(root);
    }

    var form = root.querySelector('[data-nad-interest-form]');
    var select = root.querySelector('[data-nad-interest-country]');
    var email = root.querySelector('[data-nad-interest-email]');
    var submit = root.querySelector('[data-nad-interest-submit]');
    var status = root.querySelector('[data-nad-interest-status]');
    if (!form || !select || !email || !submit || !status) return;

    function setStatus(text) {
      status.hidden = !text;
      status.textContent = text || '';
      // CH-190: the dialog can be taller than the viewport, so the success/error message can land
      // below the fold — scroll it into view so the visitor always sees the response.
      if (text) {
        try { status.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
        catch (e) { try { status.scrollIntoView(); } catch (e2) {} }
      }
    }

    select.addEventListener('change', function () {
      select.removeAttribute('aria-invalid');
      if (select.value) emit('nad:ship-to:interest-country-selected', { country: select.value });
    });
    email.addEventListener('input', function () { email.removeAttribute('aria-invalid'); });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (form.dataset.submitting === 'true') return; // duplicate-submission guard

      var valid = true;
      if (!select.value) { select.setAttribute('aria-invalid', 'true'); valid = false; }
      if (!email.value.trim() || !email.checkValidity()) { email.setAttribute('aria-invalid', 'true'); valid = false; }
      if (!valid) {
        root.querySelector('[aria-invalid="true"]').focus();
        return;
      }

      form.dataset.submitting = 'true';
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      setStatus('');

      var countryName = select.options[select.selectedIndex].textContent.trim();

      subscribe(cfg, email.value.trim(), select.value, countryName)
        .then(function () {
          setStatus(cfg.success);
          emit('nad:ship-to:interest-submitted', { country: select.value });
        })
        .catch(function () {
          setStatus(cfg.error);
          emit('nad:ship-to:interest-failed', { country: select.value });
        })
        .then(function () {
          form.dataset.submitting = 'false';
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
